// Realtime stream client. Every live cell (TR realtime fields, RtGet,
// RtStream) shares ONE WebSocket to the interface's /api/stream (JWT in the
// query string). Each cell subscribes its RICs with its own FIDs and
// unsubscribes them, with the same FIDs, when it stops; the server keeps a
// refcounted field union per (socket, RIC), so subscriptions never clash.
// The socket opens on the first subscriber, closes after the last one, and
// reconnects with backoff (re-subscribing everyone) while any remain.

import { getAccessToken, getBackendUrl } from "./auth";
import { refreshSession, logout } from "./api";

export function splitList(s: string): string[] {
  if (!s) return [];
  return s
    .split(/[,;]/)
    .map((x) => x.trim().toUpperCase())
    .filter(Boolean);
}

/** A tick's fields for one RIC (FID → value). */
export type TickCb = (ric: string, fields: Record<string, any>) => void;
/**
 * A failure. `ric` set: a per-RIC `status` frame (e.g. an unknown RIC), the
 * other RICs keep ticking. `ric` absent: the connection itself (`fatal`: it
 * will not retry, e.g. not signed in).
 */
export type ErrCb = (message: string, fatal: boolean, ric?: string) => void;

export interface RtStreamHandle {
  stop: () => void;
}

/** A RIC as compared between a cell and the stream: case aside, and `/VOD.L` for a delayed VOD.L. */
const ricId = (r: string) => String(r ?? "").trim().replace(/^\//, "").toUpperCase();

interface Subscriber {
  rics: string[];
  fields: string[];
  ids: Set<string>;
  onTick: TickCb;
  onError: ErrCb;
}

/** The subscribe/unsubscribe frame of a subscriber; no fields = all fields. */
function frame(action: "subscribe" | "unsubscribe", s: Subscriber): string {
  return JSON.stringify(s.fields.length ? { action, rics: s.rics, fields: s.fields } : { action, rics: s.rics });
}

/** Only the subscriber's own FIDs of a tick (case-insensitive); all of them when it asked for all. */
function own(s: Subscriber, fields: Record<string, any>): Record<string, any> | null {
  if (!s.fields.length) return fields;
  const wanted = new Map(s.fields.map((f) => [f.toUpperCase(), f] as [string, string]));
  const out: Record<string, any> = {};
  let any = false;
  for (const [k, v] of Object.entries(fields)) {
    const f = wanted.get(k.toUpperCase());
    if (f !== undefined) {
      out[f] = v;
      any = true;
    }
  }
  return any ? out : null;
}

/** Hub connection states of a `status` frame that are not a problem. */
const HUB_OK_STATES = new Set(["connected", "connecting"]);

/** The one shared socket and its subscribers. */
class SharedStream {
  private subs = new Set<Subscriber>();
  private ws: WebSocket | null = null;
  private connecting = false;
  private backoff = 1000;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  /** The socket was refused for its token (1008): refresh before the next connect. */
  private needsRefresh = false;
  /** Refreshed since data last arrived: a second 1008 then means the session itself is refused. */
  private refreshed = false;

  add(s: Subscriber): void {
    this.subs.add(s);
    if (this.isOpen()) this.send(frame("subscribe", s));
    else this.ensureConnected();
  }

  remove(s: Subscriber): void {
    if (!this.subs.delete(s)) return;
    if (this.isOpen()) this.send(frame("unsubscribe", s));
    if (this.subs.size === 0) this.close();
  }

  /** Drop every subscriber and the socket (tests, shutdown). */
  closeAll(): void {
    this.subs.clear();
    this.close();
  }

  private isOpen(): boolean {
    return !!this.ws && this.ws.readyState === 1; // WebSocket.OPEN
  }

  private send(data: string): void {
    try {
      this.ws?.send(data);
    } catch {
      /* the close handler reconnects and re-subscribes */
    }
  }

  private close(): void {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      ws.onclose = null;
      try {
        ws.close();
      } catch {
        /* noop */
      }
    }
  }

  private ensureConnected(): void {
    if (this.ws || this.connecting || this.reconnectTimer) return;
    this.connecting = true;
    const done = () => {
      this.connecting = false;
    };
    this.connect().then(done, (e) => {
      // Reading the token or the backend URL failed: report it, retry later.
      done();
      const message = `Live stream unavailable: ${(e && e.message) || e}`;
      this.subs.forEach((s) => s.onError(message, false));
      this.scheduleReconnect();
    });
  }

  private async connect(): Promise<void> {
    if (!this.subs.size) return;
    if (this.needsRefresh) {
      // The access token was refused (expired): get a fresh one, through the
      // same single-flight refresh as the REST calls, before connecting again.
      let ok: boolean;
      try {
        ok = await refreshSession();
      } catch (e: any) {
        // Server/network trouble: the session is kept; try again with backoff.
        this.subs.forEach((s) =>
          s.onError(`Live stream paused: session refresh failed (${(e && e.message) || e}); retrying.`, false)
        );
        this.scheduleReconnect();
        return;
      }
      if (!ok) {
        try {
          await logout();
        } catch {
          /* storage failure: the fatal error below still stops the loop */
        }
        this.needsRefresh = false;
        this.subs.forEach((s) => s.onError("Not signed in — open the LSEG task pane and sign in.", true));
        return;
      }
      this.needsRefresh = false;
      this.refreshed = true;
    }
    const token = await getAccessToken();
    if (!token) {
      this.subs.forEach((s) => s.onError("Not signed in — open the LSEG task pane and sign in.", true));
      return;
    }
    const base = await getBackendUrl();
    if (!this.subs.size || this.ws) return;
    const wsBase = base.replace(/^http/i, "ws"); // https->wss, http->ws
    let sock: WebSocket;
    try {
      sock = new WebSocket(`${wsBase}/api/stream?token=${encodeURIComponent(token)}`);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = sock;
    sock.onopen = () => {
      this.backoff = 1000;
      this.subs.forEach((s) => this.send(frame("subscribe", s)));
    };
    sock.onmessage = (ev) => {
      // The interface accepts the socket before it checks the token, so an open
      // proves nothing; data does. Only now is the session known to be good.
      this.refreshed = false;
      this.dispatch(ev.data);
    };
    sock.onclose = (ev?: { code?: number }) => {
      if (this.ws === sock) this.ws = null;
      if (!this.subs.size) return;
      if (ev?.code === 1008) {
        // Policy violation = the interface refused the token (invalid/expired,
        // or the user is disabled). Reconnecting with the same token would loop
        // silently forever, so refresh it first; if a token minted since the
        // last open is refused too, the session itself is refused: stop.
        if (this.refreshed) {
          this.refreshed = false;
          this.subs.forEach((s) => s.onError("Live stream refused the session — sign in again from the LSEG panel.", true));
          return;
        }
        this.needsRefresh = true;
      }
      this.scheduleReconnect();
    };
    sock.onerror = () => {
      try {
        sock.close();
      } catch {
        /* noop */
      }
    };
  }

  private dispatch(data: any): void {
    let msg: any;
    try {
      msg = JSON.parse(data);
    } catch {
      return;
    }
    if (msg.type === "status" && !msg.ric && typeof msg.hub === "string") {
      // The interface's hub connection state ({"type":"status","hub":"connected"}):
      // informational. Only a state other than connected/connecting is worth a warning.
      if (!HUB_OK_STATES.has(msg.hub.toLowerCase())) console.warn(`EAV: live stream hub ${msg.hub}`);
      return;
    }
    if (msg.type === "error" || (msg.type === "status" && !msg.ric)) {
      // A connection-level server error: no cell to show it in.
      console.warn(`EAV: live stream server error: ${msg.error ?? msg.message ?? JSON.stringify(msg)}`);
      return;
    }
    const id = ricId(msg.ric);
    let fields: Record<string, any> | null = null;
    if (msg.type === "tick") fields = msg.fields || {};
    else if (msg.type === "snapshot") {
      const ticks = msg.ticks || [];
      fields = ticks.length ? ticks[ticks.length - 1].fields || {} : null;
    }
    // A subscriber may stop from inside its callback: iterate over a copy.
    for (const s of Array.from(this.subs)) {
      if (!s.ids.has(id)) continue;
      const ric = s.rics.find((r) => ricId(r) === id)!;
      try {
        if (msg.type === "status") s.onError(String(msg.error ?? ""), false, ric);
        else if (fields) {
          const mine = own(s, fields);
          if (mine) s.onTick(ric, mine);
        }
      } catch (e) {
        console.error("EAV: live update failed", e);
      }
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer || !this.subs.size) return;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.ensureConnected();
    }, this.backoff);
    this.backoff = Math.min(this.backoff * 2, 15000);
  }
}

const shared = new SharedStream();

/**
 * Subscribe `instruments` (with `fields`; [] = all) on the shared socket.
 * `onTick` gets each tick's fields restricted to `fields`; `stop` unsubscribes.
 */
export function openRtStream(instruments: string[], fields: string[], onTick: TickCb, onError: ErrCb): RtStreamHandle {
  const rics = Array.from(new Map(instruments.filter(Boolean).map((r) => [ricId(r), r] as [string, string])).values());
  const sub: Subscriber = { rics, fields: fields.slice(), ids: new Set(rics.map(ricId)), onTick, onError };
  shared.add(sub);
  return { stop: () => shared.remove(sub) };
}

/** Stop every subscription and close the socket (tests). */
export function closeAllStreams(): void {
  shared.closeAll();
}

/**
 * Coalesce calls: `fn` runs at most once per `ms`, with the latest state
 * (leading call immediate, later ones at the end of the window).
 */
export function throttle(fn: () => void, ms: number): { call: () => void; cancel: () => void } {
  let last = -Infinity;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const run = () => {
    timer = null;
    last = Date.now();
    fn();
  };
  return {
    call() {
      if (timer) return;
      const wait = last + ms - Date.now();
      if (wait <= 0) run();
      else timer = setTimeout(run, wait);
    },
    cancel() {
      if (timer) clearTimeout(timer);
      timer = null;
    },
  };
}
